/**
 * DiffPanel — side-by-side compare of two attestations, highlighting
 * fields that differ. Uses the subtle background tone for "same" and
 * the Spark accent for "different".
 */

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { HashDisplay } from '@/components/HashDisplay';
import type { AttestationRecordFull } from '@/api/types';
import { useLocaleStore } from '@/stores/localeStore';
import { formatAbsoluteTime } from '@/utils/format';
import { cn } from '@/utils/cn';

import { WitnessChip } from './WitnessChip';

export function DiffPanel({
  a,
  b,
  onClose,
}: {
  a: AttestationRecordFull;
  b: AttestationRecordFull;
  onClose: () => void;
}) {
  const { t } = useTranslation('attestations');
  const locale = useLocaleStore((s) => s.locale);

  const rows: Array<{ label: string; a: React.ReactNode; b: React.ReactNode; diff: boolean }> = [
    { label: 'id', a: a.id, b: b.id, diff: a.id !== b.id },
    {
      label: 'signed_at',
      a: formatAbsoluteTime(a.signed_at.iso, locale),
      b: formatAbsoluteTime(b.signed_at.iso, locale),
      diff: a.signed_at.iso !== b.signed_at.iso,
    },
    {
      label: 'wasm_hash',
      a: <HashDisplay value={a.wasm_hash} />,
      b: <HashDisplay value={b.wasm_hash} />,
      diff: a.wasm_hash !== b.wasm_hash,
    },
    {
      label: 'input_hash',
      a: <HashDisplay value={a.input_hash} />,
      b: <HashDisplay value={b.input_hash} />,
      diff: a.input_hash !== b.input_hash,
    },
    {
      label: 'output_hash',
      a: <HashDisplay value={a.output_hash} />,
      b: <HashDisplay value={b.output_hash} />,
      diff: a.output_hash !== b.output_hash,
    },
    {
      label: 'fuel_consumed',
      a: a.fuel_consumed.toLocaleString(locale),
      b: b.fuel_consumed.toLocaleString(locale),
      diff: a.fuel_consumed !== b.fuel_consumed,
    },
    {
      label: 'bytes_in',
      a: a.bytes_in.toLocaleString(locale),
      b: b.bytes_in.toLocaleString(locale),
      diff: a.bytes_in !== b.bytes_in,
    },
    {
      label: 'bytes_out',
      a: a.bytes_out.toLocaleString(locale),
      b: b.bytes_out.toLocaleString(locale),
      diff: a.bytes_out !== b.bytes_out,
    },
    {
      label: 'witness_status',
      a: <WitnessChip status={a.witness_status} />,
      b: <WitnessChip status={b.witness_status} />,
      diff: a.witness_status !== b.witness_status,
    },
    {
      label: 'engine_signature',
      a: <HashDisplay value={a.engine_signature} />,
      b: <HashDisplay value={b.engine_signature} />,
      diff: a.engine_signature !== b.engine_signature,
    },
  ];

  return (
    <div className="rounded-xl border border-border bg-surface p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink">{t('diff.title')}</h3>
          <p className="text-xs text-ink-muted">{t('diff.sub')}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          {t('diff.close')}
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-[80px_minmax(0,1fr)_minmax(0,1fr)] gap-x-2 gap-y-1 text-xs sm:grid-cols-[140px_minmax(0,1fr)_minmax(0,1fr)] sm:gap-x-4">
        <div />
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-spark-soft text-[10px] text-spark">
            A
          </span>
          {a.execution_label}
        </div>
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-spark-soft text-[10px] text-spark">
            B
          </span>
          {b.execution_label}
        </div>

        {rows.map((r) => (
          <DiffRow key={r.label} label={r.label} a={r.a} b={r.b} diff={r.diff} />
        ))}
      </div>
    </div>
  );
}

function DiffRow({
  label,
  a,
  b,
  diff,
}: {
  label: string;
  a: React.ReactNode;
  b: React.ReactNode;
  diff: boolean;
}) {
  return (
    <>
      <div className="self-center text-[10px] uppercase tracking-wider text-ink-subtle">
        {label}
      </div>
      <div
        className={cn(
          'rounded-md px-2 py-1.5',
          diff ? 'bg-spark-soft/40 ring-1 ring-spark' : 'bg-surface-raised',
        )}
      >
        <div className="text-ink">{a}</div>
      </div>
      <div
        className={cn(
          'rounded-md px-2 py-1.5',
          diff ? 'bg-spark-soft/40 ring-1 ring-spark' : 'bg-surface-raised',
        )}
      >
        <div className="text-ink">{b}</div>
      </div>
    </>
  );
}
